import { Instance, SnapshotOut, types } from 'mobx-state-tree';

import {
  DeptApi,
  TDeleteDeptResult,
  TGetDeptResult,
  TGetDeptsResult,
  TGetDeptTreeResult,
  TPostDeptResult,
  TUpdateDeptResult,
} from '../../services';
import { createDept, Dept, IDept, IDeptFilter, IDeptSnapshot } from '../dept/Dept';
import { DeptTree, IDeptTree } from '../dept-tree/DeptTree';
import { withEnvironment } from '../extensions/with-environment';
import { withRootStore } from '../extensions/with-root-store';
import { createPagination, IPagination, IPaginationSnapshot } from '../pagination/Pagination';

/**
 * # DeptStore
 *
 * DeptStore을 설명하세요.
 */
export const DeptStore = types
  .model('DeptStore')
  // --------------------------------------------------------------------------
  .props({
    pagination: createPagination(),
    deptTree: types.optional(types.array(DeptTree), []),
    depts: types.optional(types.array(Dept), []),
    dept: createDept(),
  })
  .extend(withRootStore)
  .extend(withEnvironment)
  // eslint-disable-line @typescript-eslint/no-unused-vars
  .views((self) => ({}))
  // --------------------------------------------------------------------------
  // MUTATEs - 모델 상태를 변경
  .actions((self) => ({
    setPagination: (pagination: IPaginationSnapshot) => {
      self.pagination = pagination as IPagination;
    },
    setDeptTree: (deptTree: IDeptTree[]) => self.deptTree.replace(deptTree),
    setDepts: (depts: IDept[]) => self.depts.replace(depts),
    setDept: (depts: IDept) => (self.dept = depts),
    resetDept: () => (self.dept = {} as IDept),
    resetPagination: () => {
      self.pagination = { size: 25, page: 1 } as IPagination;
    },

    // 부서 순서 변경 함수 (moveDept)
    moveDept: (draggedId: number, targetId: number) => {
      const draggedDeptIndex = self.depts.findIndex((dept) => dept.deptSid === draggedId);
      const targetDeptIndex = self.depts.findIndex((dept) => dept.deptSid === targetId);

      if (draggedDeptIndex === -1 || targetDeptIndex === -1) {
        return; // 부서가 없으면 아무 작업도 하지 않음
      }

      const draggedDept = self.depts[draggedDeptIndex];

      // 드래그된 부서를 목록에서 제거하고, 타겟 부서의 위치에 삽입
      self.depts.splice(draggedDeptIndex, 1); // 드래그된 부서 제거
      self.depts.splice(targetDeptIndex, 0, draggedDept); // 타겟 부서 앞에 삽입
    },
  }))
  // --------------------------------------------------------------------------
  // REQUESTs - 서비스 요청 및 기타 인터페이스 요청
  .actions((self) => ({
    setPagination: (pagination: IPaginationSnapshot) => {
      self.pagination = pagination as IPagination;
    },
    getTree: async (menuSid?: number, snapHistSid?: number) => {
      const deptApi: DeptApi = new DeptApi(self.environment.api);
      const result: TGetDeptTreeResult = await deptApi.getTree(menuSid, snapHistSid);
      self.rootStore.responseStore.getResponseResult(result);
      if (result.kind === 'ok') {
        const sortTree = (tree: IDeptTree[]): IDeptTree[] => {
          return tree
            .slice() // 원본 배열 보호
            .sort((a, b) => {
              // ordr로 1차 정렬, 같으면 deptSid로 2차 정렬
              const ordrDiff = a.ordr - b.ordr;
              if (ordrDiff !== 0) return ordrDiff;
              return a.deptSid - b.deptSid;
            })
            .sort((a, b) => (a.deptSid === 999 ? 1 : b.deptSid === 999 ? -1 : 0)) // 기타부서를 마지막으로 정렬
            .map((node) => ({
              ...node,
              childDept: node.childDept ? sortTree(node.childDept) : [], // 재귀적으로 하위 부서 정렬
            }));
        };

        self.setDeptTree(sortTree(result.deptTree));
      } else {
        console.error(result.kind);
      }
    },
    gets: async (filter?: IDeptFilter) => {
      const deptApi: DeptApi = new DeptApi(self.environment.api);
      const result: TGetDeptsResult = await deptApi.gets(self.pagination, filter);
      self.rootStore.responseStore.getResponseResult(result);
      if (result.kind === 'ok') {
        self.setDepts(result.depts);
      } else {
        console.error(result.kind);
      }
    },
    get: async (deptSid: number) => {
      const deptApi: DeptApi = new DeptApi(self.environment.api);
      const result: TGetDeptResult = await deptApi.get(deptSid);
      self.rootStore.responseStore.getResponseResult(result);
      if (result.kind === 'ok') {
        self.setDept(result.dept);
      } else {
        console.error(result.kind);
      }
    },
    post: async (data: IDeptSnapshot) => {
      const deptApi: DeptApi = new DeptApi(self.environment.api);
      const result: TPostDeptResult = await deptApi.post(data);
      self.rootStore.responseStore.getResponseResult(result);
      if (result.kind === 'ok') {
        self.setDept(result.dept);
      } else {
        console.error(result.kind);
      }
    },
    update: async (data: IDeptSnapshot) => {
      const deptApi: DeptApi = new DeptApi(self.environment.api);
      const result: TUpdateDeptResult = await deptApi.update(data);
      self.rootStore.responseStore.getResponseResult(result);
      if (result.kind === 'ok') {
        self.setDept(result.dept);
      } else {
        console.error(result.kind);
      }
    },
    delete: async (deptSid: number) => {
      const deptApi: DeptApi = new DeptApi(self.environment.api);
      const result: TDeleteDeptResult = await deptApi.delete(deptSid);
      self.rootStore.responseStore.getResponseResult(result);
      if (result.kind === 'ok') {
        return;
      } else {
        console.error(result.kind);
      }
    },
  }));

// --------------------------------------------------------------------------
type TDeptStore = Instance<typeof DeptStore>;
type TDeptStoreSnapshot = SnapshotOut<typeof DeptStore>;

export interface IDeptStore extends TDeptStore {}
export type TDeptStoreKeys = keyof TDeptStoreSnapshot & string;
export interface IDeptStoreSnapshot extends TDeptStoreSnapshot {}
export const createDeptStore = () => types.optional(DeptStore, {} as TDeptStore);
